import { useEffect, useState, useCallback, useMemo } from "react";
import { Plus, Pencil, Trash2, X, Clock, Users, Globe2, CalendarClock, Building2 } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { crmApi, logDevError } from "../../lib/crm";
import {
  getZonedParts,
  availabilityFromHour,
  availabilityForZone,
  offsetFromAmsterdam,
  findMeetingSlots,
  formatTimeInTz,
  IANA_TIMEZONES,
} from "../../lib/timezones";
import { CITY_TO_COUNTRY, COUNTRY_TIMEZONE_MAP, countryToTimezone } from "../../lib/countryTimezones";
import { toast } from "sonner";

export default function TimezoneManager() {
  const [config, setConfig] = useState(null);
  const [agents, setAgents] = useState([]);
  const [zones, setZones] = useState([]);
  const [crmAgents, setCrmAgents] = useState([]);
  const [now, setNow] = useState(new Date());
  const [editingAgent, setEditingAgent] = useState(null);
  const [editingZone, setEditingZone] = useState(null);
  const [showNewAgent, setShowNewAgent] = useState(false);
  const [showNewZone, setShowNewZone] = useState(false);

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(id);
  }, []);

  const fetchAll = useCallback(async () => {
    try {
      const [cfg, ags, zs, crm] = await Promise.all([
        crmApi.get("/timezones/config"),
        crmApi.get("/timezones/agents"),
        crmApi.get("/timezones/lead-zones"),
        crmApi.get("/agents"),
      ]);
      setConfig(cfg.data);
      setAgents(ags.data);
      setZones(zs.data);
      setCrmAgents(crm.data);
    } catch (err) {
      logDevError("TimezoneManager.fetchAll", err);
      toast.error("Error cargando datos");
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  if (!config) return <div className="p-8 text-white/40 text-sm">Cargando…</div>;

  const amsParts = getZonedParts(config.management_timezone, now);
  const amsAvail = amsParts ? availabilityFromHour(amsParts.hour) : null;

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="tz-admin-root">
      <header>
        <h1 className="text-3xl font-bold tracking-tight">Timezone Manager</h1>
        <p className="text-sm text-white/40 mt-1">
          Centro de control horario · {agents.length} agentes · {zones.length} zonas de leads
        </p>
      </header>

      {/* Block 1 — Management clock (sticky) */}
      <div
        data-testid="block-management"
        className="sticky top-16 z-20 glass-strong rounded-2xl p-6 border border-lime-400/30 bg-gradient-to-br from-lime-400/10 to-transparent"
      >
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <span
              className="w-3 h-3 rounded-full"
              style={{ background: amsAvail?.color || "#64748b", boxShadow: `0 0 12px ${amsAvail?.color || "#64748b"}` }}
            />
            <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-lime-300 mono">
              {config.management_city} · Gerencia
            </div>
          </div>
          <div className="flex items-baseline gap-3 ml-auto">
            <div className="text-5xl font-bold text-white mono tracking-tight" data-testid="ams-time">
              {amsParts?.time || "—"}
            </div>
            <div className="text-sm text-white/50 mono">{amsParts?.date}</div>
          </div>
          <div className="text-xs px-2 py-1 rounded-full" style={{
            background: (amsAvail?.color || "#64748b") + "20",
            color: amsAvail?.color || "#64748b",
          }}>
            {amsAvail?.label_es}
          </div>
        </div>
      </div>

      {/* Block 2 — Agents */}
      <Section
        icon={Users}
        title="Agentes de Venta"
        subtitle={`${agents.length} agentes registrados`}
        action={
          <Button
            onClick={() => setShowNewAgent(true)}
            data-testid="new-tz-agent-btn"
            className="bg-lime-400 hover:bg-lime-300 text-white"
            size="sm"
          >
            <Plus className="w-4 h-4 mr-1" /> Nuevo agente
          </Button>
        }
      >
        {agents.length === 0 ? (
          <EmptyMessage text="No hay agentes registrados aún" />
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {agents.map((a) => (
              <TZAgentCard
                key={a.id}
                agent={a}
                allZones={zones}
                crmAgents={crmAgents}
                now={now}
                amsTz={config.management_timezone}
                onEdit={() => setEditingAgent(a)}
                onDelete={async () => {
                  if (!window.confirm(`¿Eliminar agente "${a.name}"?`)) return;
                  try {
                    await crmApi.delete(`/timezones/agents/${a.id}`);
                    toast.success("Agente eliminado");
                    fetchAll();
                  } catch (err) {
                    logDevError("delete agent", err);
                    toast.error("Error eliminando");
                  }
                }}
                onAssignChange={async (newZoneIds) => {
                  try {
                    await crmApi.put(`/timezones/agents/${a.id}`, { lead_zone_ids: newZoneIds });
                    fetchAll();
                  } catch (err) {
                    logDevError("update assignment", err);
                    toast.error("Error guardando asignación");
                  }
                }}
              />
            ))}
          </div>
        )}
      </Section>

      {/* Block 3 — Lead zones */}
      <Section
        icon={Globe2}
        title="Zonas de Leads"
        subtitle={`${zones.length} zonas configuradas`}
        action={
          <Button
            onClick={() => setShowNewZone(true)}
            data-testid="new-tz-zone-btn"
            className="bg-lime-400 hover:bg-lime-300 text-white"
            size="sm"
          >
            <Plus className="w-4 h-4 mr-1" /> Nueva zona
          </Button>
        }
      >
        {zones.length === 0 ? (
          <EmptyMessage text="No hay zonas de leads configuradas" />
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {zones.map((z) => (
              <TZZoneCard
                key={z.id}
                zone={z}
                now={now}
                amsTz={config.management_timezone}
                onEdit={() => setEditingZone(z)}
                onDelete={async () => {
                  if (!window.confirm(`¿Eliminar zona "${z.name}"?`)) return;
                  try {
                    await crmApi.delete(`/timezones/lead-zones/${z.id}`);
                    toast.success("Zona eliminada");
                    fetchAll();
                  } catch (err) {
                    logDevError("delete zone", err);
                    toast.error("Error eliminando");
                  }
                }}
              />
            ))}
          </div>
        )}
      </Section>

      {/* Block 4 — Meeting Planner */}
      <Section icon={CalendarClock} title="Meeting Planner" subtitle="Calcula ventanas comunes Gerencia + Agente + Lead">
        <MeetingPlanner agents={agents} zones={zones} amsTz={config.management_timezone} />
      </Section>

      {/* Dialogs */}
      {showNewAgent && (
        <AgentDialog
          onClose={() => setShowNewAgent(false)}
          onSaved={() => { setShowNewAgent(false); fetchAll(); }}
          crmAgents={crmAgents}
          zones={zones}
        />
      )}
      {editingAgent && (
        <AgentDialog
          existing={editingAgent}
          onClose={() => setEditingAgent(null)}
          onSaved={() => { setEditingAgent(null); fetchAll(); }}
          crmAgents={crmAgents}
          zones={zones}
        />
      )}
      {showNewZone && (
        <ZoneDialog
          onClose={() => setShowNewZone(false)}
          onSaved={() => { setShowNewZone(false); fetchAll(); }}
        />
      )}
      {editingZone && (
        <ZoneDialog
          existing={editingZone}
          onClose={() => setEditingZone(null)}
          onSaved={() => { setEditingZone(null); fetchAll(); }}
        />
      )}
    </div>
  );
}

// ============================================================
// Reusable section wrapper
// ============================================================
function Section({ icon: Icon, title, subtitle, action, children }) {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-white/5 border border-white/8 flex items-center justify-center">
            <Icon className="w-4 h-4 text-white/70" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-white">{title}</h2>
            {subtitle && <p className="text-xs text-white/40 mono">{subtitle}</p>}
          </div>
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

function EmptyMessage({ text }) {
  return (
    <div className="text-center py-8 text-white/40 text-sm bg-white/[0.02] rounded-xl border border-white/5">
      {text}
    </div>
  );
}

// ============================================================
// TZ Agent card
// ============================================================
function TZAgentCard({ agent, allZones, crmAgents, now, amsTz, onEdit, onDelete, onAssignChange }) {
  const [showAssign, setShowAssign] = useState(false);
  const parts = getZonedParts(agent.timezone, now);
  const avail = parts ? availabilityFromHour(parts.hour) : null;
  const diff = offsetFromAmsterdam(agent.timezone, amsTz, now);
  const linkedAgentName = useMemo(() => {
    const m = crmAgents.find((a) => a.id === agent.linked_crm_agent_id);
    return m?.full_name || null;
  }, [crmAgents, agent.linked_crm_agent_id]);
  const assignedZoneNames = useMemo(() =>
    (agent.lead_zone_ids || [])
      .map((id) => allZones.find((z) => z.id === id)?.name)
      .filter(Boolean),
  [agent.lead_zone_ids, allZones]);

  return (
    <div
      data-testid={`tz-agent-card-${agent.id}`}
      className="glass-strong rounded-xl p-4 border-2 hover:border-white/20 transition"
      style={{ borderColor: avail ? avail.color + "60" : "rgba(255,255,255,0.08)" }}
    >
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full" style={{ background: avail?.color }} />
            <div className="font-bold text-white truncate">{agent.name}</div>
          </div>
          <div className="text-xs text-white/50 mono mt-0.5">{agent.timezone}</div>
          {linkedAgentName && (
            <div className="text-[10px] text-lime-300 mt-1">→ {linkedAgentName}</div>
          )}
        </div>
        <div className="flex gap-1">
          <button onClick={onEdit} className="text-white/40 hover:text-white p-1" data-testid={`edit-agent-${agent.id}`}>
            <Pencil className="w-3.5 h-3.5" />
          </button>
          <button onClick={onDelete} className="text-white/40 hover:text-red-400 p-1" data-testid={`delete-agent-${agent.id}`}>
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      <div className="flex items-baseline justify-between mb-3">
        <div className="text-2xl font-bold text-white mono">{parts?.time || "—"}</div>
        <div className="text-xs text-white/50 mono">{diff} vs AMS</div>
      </div>

      <div className="text-[10px] uppercase tracking-wider text-white/40 mono mb-1.5">
        Leads asignados ({assignedZoneNames.length})
      </div>
      {assignedZoneNames.length === 0 ? (
        <div className="text-xs text-white/40 italic">Sin zonas asignadas</div>
      ) : (
        <div className="flex flex-wrap gap-1">
          {assignedZoneNames.map((n) => (
            <span key={n} className="text-[10px] px-1.5 py-0.5 rounded bg-lime-400/15 text-lime-300 border border-lime-400/20">
              {n}
            </span>
          ))}
        </div>
      )}

      <button
        onClick={() => setShowAssign((v) => !v)}
        data-testid={`assign-zones-${agent.id}`}
        className="mt-3 text-xs text-lime-400 hover:text-lime-300"
      >
        {showAssign ? "Ocultar" : "Asignar zonas →"}
      </button>

      {showAssign && (
        <div className="mt-3 p-3 rounded-lg bg-white/5 border border-white/8 space-y-1.5">
          {allZones.length === 0 ? (
            <div className="text-xs text-white/40">No hay zonas creadas todavía</div>
          ) : allZones.map((z) => {
            const checked = (agent.lead_zone_ids || []).includes(z.id);
            return (
              <label key={z.id} className="flex items-center gap-2 text-xs cursor-pointer">
                <input
                  type="checkbox"
                  checked={checked}
                  data-testid={`assign-toggle-${agent.id}-${z.id}`}
                  onChange={(e) => {
                    const current = agent.lead_zone_ids || [];
                    const next = e.target.checked
                      ? [...current, z.id]
                      : current.filter((x) => x !== z.id);
                    onAssignChange(next);
                  }}
                />
                <span className="text-white/80">{z.name}</span>
                <span className="text-white/40 mono">({z.timezone})</span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ============================================================
// TZ Zone card
// ============================================================
function TZZoneCard({ zone, now, amsTz, onEdit, onDelete }) {
  const parts = getZonedParts(zone.timezone, now);
  const avail = parts ? availabilityFromHour(parts.hour) : null;
  const diff = offsetFromAmsterdam(zone.timezone, amsTz, now);

  return (
    <div
      data-testid={`tz-zone-card-${zone.id}`}
      className="glass-strong rounded-xl p-4 border-2 hover:border-white/20 transition"
      style={{ borderColor: avail ? avail.color + "60" : "rgba(255,255,255,0.08)" }}
    >
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full" style={{ background: avail?.color }} />
            <div className="font-bold text-white truncate">{zone.name}</div>
          </div>
          <div className="text-xs text-white/50 mono mt-0.5">{zone.timezone}</div>
        </div>
        <div className="flex gap-1">
          <button onClick={onEdit} className="text-white/40 hover:text-white p-1" data-testid={`edit-zone-${zone.id}`}>
            <Pencil className="w-3.5 h-3.5" />
          </button>
          <button onClick={onDelete} className="text-white/40 hover:text-red-400 p-1" data-testid={`delete-zone-${zone.id}`}>
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
      <div className="flex items-baseline justify-between">
        <div className="text-2xl font-bold text-white mono">{parts?.time || "—"}</div>
        <div className="text-xs text-white/50 mono">{diff} vs AMS</div>
      </div>
      <div className="mt-2 text-[10px] px-2 py-0.5 inline-block rounded-full" style={{
        background: (avail?.color || "#64748b") + "20",
        color: avail?.color || "#64748b",
      }}>
        {avail?.label_es}
      </div>
    </div>
  );
}

// ============================================================
// Meeting Planner (Admin: picks agent + zone)
// ============================================================
function MeetingPlanner({ agents, zones, amsTz }) {
  const [agentId, setAgentId] = useState("");
  const [zoneId, setZoneId] = useState("");

  const agent = agents.find((a) => a.id === agentId);
  const zone = zones.find((z) => z.id === zoneId);

  const tzs = [amsTz];
  if (agent) tzs.push(agent.timezone);
  if (zone) tzs.push(zone.timezone);

  return (
    <div className="glass-strong rounded-xl p-5 border border-white/8 space-y-4">
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-white/70 text-xs">Agente</Label>
          <select
            value={agentId}
            onChange={(e) => setAgentId(e.target.value)}
            data-testid="planner-agent-select"
            className="w-full bg-white/5 border border-white/10 text-white text-sm rounded-md px-3 py-2 mt-1"
          >
            <option value="">— Selecciona un agente —</option>
            {agents.map((a) => (
              <option key={a.id} value={a.id}>{a.name} ({a.timezone})</option>
            ))}
          </select>
        </div>
        <div>
          <Label className="text-white/70 text-xs">Zona de Lead</Label>
          <select
            value={zoneId}
            onChange={(e) => setZoneId(e.target.value)}
            data-testid="planner-zone-select"
            className="w-full bg-white/5 border border-white/10 text-white text-sm rounded-md px-3 py-2 mt-1"
          >
            <option value="">— Selecciona una zona —</option>
            {zones.map((z) => (
              <option key={z.id} value={z.id}>{z.name} ({z.timezone})</option>
            ))}
          </select>
        </div>
      </div>
      <PlannerResult tzs={tzs} amsTz={amsTz} agent={agent} zone={zone} />
    </div>
  );
}

// Shared planner result display
export function PlannerResult({ tzs, amsTz, agent, zone }) {
  const enoughInputs = tzs.length >= 3;
  const { slots, days_checked } = useMemo(
    () => (enoughInputs ? findMeetingSlots(tzs, { stepMin: 30, days: 7 }) : { slots: [], days_checked: 0 }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tzs.join("|"), enoughInputs]
  );

  if (!enoughInputs) {
    return (
      <div className="text-sm text-white/50 italic">
        Selecciona agente y zona para ver ventanas disponibles.
      </div>
    );
  }

  const labels = [
    { tz: amsTz, label: "Gerencia (AMS)" },
    ...(agent ? [{ tz: agent.timezone, label: `Agente · ${agent.name}` }] : []),
    ...(zone ? [{ tz: zone.timezone, label: `Lead · ${zone.name}` }] : []),
  ];

  if (slots.length === 0) {
    return (
      <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30 text-sm text-red-200" data-testid="planner-empty">
        Sin ventana común en los próximos {days_checked} días dentro del horario laboral 09–17 de las tres zonas.
        Considera ajustar horarios o usar reuniones asincrónicas.
      </div>
    );
  }

  const sameDay = new Date(slots[0].start).toDateString() === new Date().toDateString();

  return (
    <div className="space-y-3" data-testid="planner-result">
      <div className="text-xs text-white/60">
        {sameDay
          ? <span className="text-emerald-300 font-semibold">✓ Hoy hay {slots.length} ventana(s) disponible(s):</span>
          : <span className="text-amber-300 font-semibold">
              ⚠ No hay ventana hoy. Próximo día con disponibilidad: {new Date(slots[0].start).toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long" })}
            </span>}
      </div>
      <div className="space-y-2">
        {slots.map((s) => (
          <div
            key={s.start.toISOString()}
            data-testid="planner-slot"
            className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30"
          >
            <div className="grid sm:grid-cols-3 gap-2 text-xs">
              {labels.map((l) => (
                <div key={l.tz} className="space-y-0.5">
                  <div className="text-[10px] uppercase tracking-wider text-white/40 mono">{l.label}</div>
                  <div className="text-sm font-bold text-white mono">
                    {formatTimeInTz(s.start, l.tz)}–{formatTimeInTz(s.end, l.tz)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================
// Dialogs
// ============================================================
function AgentDialog({ existing, onClose, onSaved, crmAgents, zones }) {
  const [form, setForm] = useState({
    name: existing?.name || "",
    city: existing?.city || "",
    timezone: existing?.timezone || "Europe/Amsterdam",
    linked_crm_agent_id: existing?.linked_crm_agent_id || "",
    lead_zone_ids: existing?.lead_zone_ids || [],
  });
  const [saving, setSaving] = useState(false);
  const [tzManual, setTzManual] = useState(false); // collapsed by default

  // Resolve a timezone from a free-text city or country string.
  const inferTzFrom = (text) => {
    if (!text) return null;
    const trimmed = text.trim();
    const lower = trimmed.toLowerCase();
    // 1) city → country code → tz
    const code = CITY_TO_COUNTRY[lower];
    if (code && COUNTRY_TIMEZONE_MAP[code]) {
      return { tz: COUNTRY_TIMEZONE_MAP[code].tz, city: COUNTRY_TIMEZONE_MAP[code].city };
    }
    // 2) Treat the string as a country name
    const resolved = countryToTimezone(trimmed);
    return resolved?.tz ? { tz: resolved.tz, city: resolved.city } : null;
  };

  // Auto-resolve TZ when the user types the reference city.
  const onCityChange = (v) => {
    setForm((f) => {
      const next = { ...f, city: v };
      const inf = inferTzFrom(v);
      if (inf?.tz) next.timezone = inf.tz;
      return next;
    });
  };

  // Auto-fill from the linked CRM agent's profile when the admin picks one.
  const onLinkChange = (agentId) => {
    setForm((f) => {
      const next = { ...f, linked_crm_agent_id: agentId };
      if (!agentId) return next;
      const ag = crmAgents.find((a) => a.id === agentId);
      if (!ag) return next;
      // Pull as much as possible from the existing CRM agent
      if (!f.name && ag.full_name) next.name = ag.full_name;
      if (ag.timezone) next.timezone = ag.timezone;
      if (!f.city) {
        // Prefer the agent's country (mapped to a default city) over leaving it blank
        const inf = inferTzFrom(ag.country || "");
        next.city = inf?.city || ag.country || "";
        if (!ag.timezone && inf?.tz) next.timezone = inf.tz;
      }
      return next;
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.linked_crm_agent_id) payload.linked_crm_agent_id = null;
      if (existing) {
        await crmApi.put(`/timezones/agents/${existing.id}`, payload);
        toast.success("Agente actualizado");
      } else {
        await crmApi.post("/timezones/agents", payload);
        toast.success("Agente creado");
      }
      onSaved();
    } catch (err) {
      logDevError("save agent", err);
      toast.error("Error guardando");
    } finally {
      setSaving(false);
    }
  };

  const inferredFromCity = inferTzFrom(form.city);

  return (
    <DialogShell title={existing ? "Editar agente" : "Nuevo agente"} onClose={onClose}>
      <form onSubmit={submit} className="space-y-3" data-testid="tz-agent-form">
        {/* Quick-fill from CRM (moved to top so admin uses it FIRST) */}
        <div>
          <Label className="text-white/70 text-xs">Vincular con agente CRM (opcional)</Label>
          <select
            value={form.linked_crm_agent_id || ""}
            onChange={(e) => onLinkChange(e.target.value)}
            data-testid="tz-agent-link"
            className="w-full bg-white/5 border border-white/10 text-white text-sm rounded-md px-3 py-2 mt-1"
          >
            <option value="">— No vincular —</option>
            {crmAgents.map((a) => (
              <option key={a.id} value={a.id}>{a.full_name} ({a.email})</option>
            ))}
          </select>
          <p className="text-[10px] text-white/40 mt-1">
            Selecciona un agente para rellenar nombre, ciudad y zona horaria automáticamente.
          </p>
        </div>
        <Field label="Nombre del agente *" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="tz-agent-name" required />
        <Field
          label="Ciudad o país de referencia *"
          value={form.city}
          onChange={onCityChange}
          testid="tz-agent-city"
          required
        />
        {/* Auto-detected timezone preview */}
        <div className="flex items-center justify-between gap-2 px-2 py-2 rounded-md bg-lime-400/10 border border-lime-400/30">
          <div className="text-xs">
            <span className="text-white/60">Zona horaria detectada: </span>
            <span className="text-lime-300 mono">{form.timezone || "—"}</span>
            {inferredFromCity?.city && form.timezone === inferredFromCity.tz && (
              <span className="text-white/40"> · {inferredFromCity.city}</span>
            )}
          </div>
          <button
            type="button"
            onClick={() => setTzManual((v) => !v)}
            data-testid="tz-toggle-manual"
            className="text-[10px] uppercase tracking-wider text-white/50 hover:text-lime-300"
          >
            {tzManual ? "Cancelar" : "Cambiar manualmente"}
          </button>
        </div>
        {tzManual && (
          <SelectField
            label="Zona horaria (manual)"
            value={form.timezone}
            onChange={(v) => setForm({ ...form, timezone: v })}
            options={IANA_TIMEZONES}
            testid="tz-agent-timezone"
          />
        )}
        <div>
          <Label className="text-white/70 text-xs">Zonas de leads asignadas</Label>
          <div className="mt-1 p-3 rounded-lg bg-white/5 border border-white/8 max-h-40 overflow-y-auto space-y-1.5">
            {zones.length === 0 ? (
              <div className="text-xs text-white/40">No hay zonas creadas todavía</div>
            ) : zones.map((z) => {
              const checked = form.lead_zone_ids.includes(z.id);
              return (
                <label key={z.id} className="flex items-center gap-2 text-xs cursor-pointer">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => {
                      const next = e.target.checked
                        ? [...form.lead_zone_ids, z.id]
                        : form.lead_zone_ids.filter((x) => x !== z.id);
                      setForm({ ...form, lead_zone_ids: next });
                    }}
                  />
                  <span className="text-white/80">{z.name}</span>
                </label>
              );
            })}
          </div>
        </div>
        <div className="flex gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onClose} className="border-white/10 text-white/80">Cancelar</Button>
          <Button type="submit" disabled={saving} data-testid="tz-agent-submit" className="flex-1 bg-lime-400 hover:bg-lime-300 text-white">
            {saving ? "Guardando…" : "Guardar"}
          </Button>
        </div>
      </form>
    </DialogShell>
  );
}

function ZoneDialog({ existing, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: existing?.name || "",
    timezone: existing?.timezone || "Europe/Amsterdam",
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (existing) {
        await crmApi.put(`/timezones/lead-zones/${existing.id}`, form);
        toast.success("Zona actualizada");
      } else {
        await crmApi.post("/timezones/lead-zones", form);
        toast.success("Zona creada");
      }
      onSaved();
    } catch (err) {
      logDevError("save zone", err);
      toast.error("Error guardando");
    } finally {
      setSaving(false);
    }
  };

  return (
    <DialogShell title={existing ? "Editar zona" : "Nueva zona de lead"} onClose={onClose}>
      <form onSubmit={submit} className="space-y-3" data-testid="tz-zone-form">
        <Field label="Nombre de la región *" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="tz-zone-name" required />
        <SelectField
          label="Zona horaria *"
          value={form.timezone}
          onChange={(v) => setForm({ ...form, timezone: v })}
          options={IANA_TIMEZONES}
          testid="tz-zone-timezone"
        />
        <div className="flex gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onClose} className="border-white/10 text-white/80">Cancelar</Button>
          <Button type="submit" disabled={saving} data-testid="tz-zone-submit" className="flex-1 bg-lime-400 hover:bg-lime-300 text-white">
            {saving ? "Guardando…" : "Guardar"}
          </Button>
        </div>
      </form>
    </DialogShell>
  );
}

function DialogShell({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="glass-strong rounded-2xl p-6 w-full max-w-md border border-white/10 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-bold">{title}</h2>
          <button onClick={onClose} className="text-white/50 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testid, required }) {
  return (
    <div>
      <Label className="text-white/70 text-xs">{label}</Label>
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        data-testid={testid}
        className="bg-white/5 border-white/10 text-white mt-1"
      />
    </div>
  );
}

function SelectField({ label, value, onChange, options, testid }) {
  return (
    <div>
      <Label className="text-white/70 text-xs">{label}</Label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full bg-white/5 border border-white/10 text-white text-sm rounded-md px-3 py-2 mt-1"
      >
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}
